import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  booleanAttribute,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Editor, defaultValueCtx, editorViewCtx, editorViewOptionsCtx, rootCtx } from '@milkdown/kit/core';
import { clipboard } from '@milkdown/kit/plugin/clipboard';
import { history, redoCommand, undoCommand } from '@milkdown/kit/plugin/history';
import { listener, listenerCtx } from '@milkdown/kit/plugin/listener';
import {
  commonmark,
  createCodeBlockCommand,
  insertImageCommand,
  toggleEmphasisCommand,
  toggleLinkCommand,
  toggleStrongCommand,
  wrapInBlockquoteCommand,
  wrapInBulletListCommand,
  wrapInHeadingCommand,
  wrapInOrderedListCommand,
} from '@milkdown/kit/preset/commonmark';
import { gfm, insertTableCommand, toggleStrikethroughCommand } from '@milkdown/kit/preset/gfm';
import { callCommand, getMarkdown, insert, replaceAll } from '@milkdown/kit/utils';
import { MaterialModule } from '../../material.module';
import { JkMinioService, MinioImageItem } from '../../services/jk-minio.service';
import { markdownToolIcon } from './icons.js';

@Component({
  selector: 'app-milkdown-markdown-editor',
  standalone: true,
  templateUrl: './milkdown-markdown-editor.component.html',
  styleUrl: './milkdown-markdown-editor.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MilkdownMarkdownEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MilkdownMarkdownEditorComponent),
      multi: true,
    },
  ],
  imports:[MaterialModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MilkdownMarkdownEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor, Validator {
  private readonly sanitizer = inject(DomSanitizer);
  readonly markdownToolIcon = {
    bold: this.toSafeHtml(markdownToolIcon.bold),
    italic: this.toSafeHtml(markdownToolIcon.italic),
    strike: this.toSafeHtml(markdownToolIcon.strike),
    heading1: this.toSafeHtml(markdownToolIcon.header['1']),
    heading2: this.toSafeHtml(markdownToolIcon.header['2']),
    heading3: this.toSafeHtml(markdownToolIcon.header['3']),
    bulletList: this.toSafeHtml(markdownToolIcon.list.bullet),
    orderedList: this.toSafeHtml(markdownToolIcon.list.ordered),
    taskList: this.toSafeHtml(markdownToolIcon.list.check),
    blockquote: this.toSafeHtml(markdownToolIcon.blockquote),
    codeIcon: this.toSafeHtml(markdownToolIcon.codeIcon),
    table: this.toSafeHtml(markdownToolIcon.table),
    link: this.toSafeHtml(markdownToolIcon.link),
    image: this.toSafeHtml(markdownToolIcon.image),
    copy: this.toSafeHtml(markdownToolIcon.copy),
    paste: this.toSafeHtml(markdownToolIcon.paste),
    source: this.toSafeHtml(markdownToolIcon.source),
    sun: this.toSafeHtml(markdownToolIcon.sun),
    moon: this.toSafeHtml(markdownToolIcon.moon),
    fullscreen: this.toSafeHtml(markdownToolIcon.fullscreen),
    undo: this.toSafeHtml(markdownToolIcon.undo),
    redo: this.toSafeHtml(markdownToolIcon.redo),
  };
  async copyToClipboard(): Promise<void> {
    if (this.destroyed) {
      return;
    }

    try {
      const markdown = this.editorReady && this.editor ? this.editor.action(getMarkdown()) : this.value;
      await navigator.clipboard.writeText(markdown);
    } catch (error) {
      console.error('複製 Markdown 失敗', error);
      window.alert('無法複製，請確認使用 HTTPS 或 localhost，並允許瀏覽器存取剪貼簿。');
    }
  }

  async pastFromClipboard(): Promise<void> {
    if (this.destroyed || !this.editorReady || this.isDisabled || this.isReadonly) {
      return;
    }

    try {
      const markdown = await navigator.clipboard.readText();
      if (!markdown || this.destroyed) {
        return;
      }

      this.runCommand(() => {
        const editor = this.editor;
        if (!editor) {
          return;
        }

        editor.action(insert(markdown, true));
        this.setMarkdown(editor.action(getMarkdown()), true);
        editor.action((ctx) => ctx.get(editorViewCtx).focus());
        this.onTouched();
      });
    } catch (error) {
      console.error('貼上 Markdown 失敗', error);
      if (!this.destroyed) {
        window.alert('無法貼上，請允許瀏覽器讀取剪貼簿，或在編輯器內按 Ctrl+V／Command+V。');
      }
    }
  }
showText() {
  this.showTextFlag.set(!this.showTextFlag());
}
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly minioService = inject(JkMinioService);

  @ViewChild('imageInput', { static: true })
  private readonly imageInput!: ElementRef<HTMLInputElement>;

  readonly host = input<string>('');
  readonly path = input<string>('');
  protected imageUploading = false;
  protected showMinioImages = false;
  protected minioImagesLoading = false;
  protected minioImages: MinioImageItem[] = [];
  private value = '';
  private editor: Editor | undefined;
  private editorReady = false;
  private destroyed = false;
  private applyingExternalValue = false;
  private onChange: (markdown: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  @ViewChild('editorHost', { static: true })
  private readonly editorHost!: ElementRef<HTMLDivElement>;

  protected isFullscreen = false;
  protected isDarkMode = false;
  protected isDisabled = false;
  protected isReadonly = false;
  protected isRequired = false;
  protected editorPlaceholder = '開始輸入 Markdown…';

  readonly markdownChange = output<string>();
  readonly fullscreenChange = output<boolean>();
  readonly showToolbar = input(true);
  showTextFlag = signal(false);

  @Input()
  set markdown(markdown: string | null | undefined) {
    this.setMarkdown(markdown ?? '', false);
  }

  get markdown(): string {
    return this.value;
  }

  @Input()
  set placeholder(value: string) {
    this.editorPlaceholder = value || '開始輸入 Markdown…';
    this.updateEditableState();
  }

  @Input({ transform: booleanAttribute })
  set disabled(value: boolean) {
    this.isDisabled = value;
    this.updateEditableState();
  }

  @Input({ transform: booleanAttribute })
  set readonly(value: boolean) {
    this.isReadonly = value;
    this.updateEditableState();
  }

  @Input({ transform: booleanAttribute })
  set required(value: boolean) {
    this.isRequired = value;
    this.onValidatorChange();
    this.updateEditableState();
  }

  @Input({ transform: booleanAttribute })
  set darkMode(value: boolean) {
    this.isDarkMode = value;
  }

  @Input({ transform: booleanAttribute })
  set fullscreen(value: boolean) {
    this.isFullscreen = value;
  }

  ngAfterViewInit(): void {
    void this.createEditor();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.editorReady) {
      void this.editor?.destroy();
    }
    this.editorReady = false;
  }

  writeValue(markdown: string | null): void {
    this.setMarkdown(markdown ?? '', false);
  }

  registerOnChange(fn: (markdown: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors | null {
    return this.isRequired && !this.value.trim() ? { required: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  protected toggleBold(): void {
    this.runCommand(() => this.editor?.action(callCommand(toggleStrongCommand.key)));
  }

  protected toggleItalic(): void {
    this.runCommand(() => this.editor?.action(callCommand(toggleEmphasisCommand.key)));
  }

  protected toggleStrike(): void {
    this.runCommand(() => this.editor?.action(callCommand(toggleStrikethroughCommand.key)));
  }

  protected toggleHeading(level: 1 | 2 | 3): void {
    this.runCommand(() => this.editor?.action(callCommand(wrapInHeadingCommand.key, level)));
  }

  protected toggleBulletList(): void {
    this.runCommand(() => this.editor?.action(callCommand(wrapInBulletListCommand.key)));
  }

  protected toggleOrderedList(): void {
    this.runCommand(() => this.editor?.action(callCommand(wrapInOrderedListCommand.key)));
  }

  protected insertTask(): void {
    this.runCommand(() => {
      const editor = this.editor;
      if (!editor) {
        return;
      }

      const view = editor.action((ctx) => ctx.get(editorViewCtx));
      let { $from } = view.state.selection;

      for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        if (node.type.name !== 'list_item' || node.attrs['checked'] == null) {
          continue;
        }

        view.dispatch(
          view.state.tr.setNodeMarkup($from.before(depth), undefined, {
            ...node.attrs,
            checked: !node.attrs['checked'],
          }),
        );
        view.focus();
        return;
      }

      editor.action(callCommand(wrapInBulletListCommand.key));
      $from = view.state.selection.$from;
      for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        if (node.type.name !== 'list_item') {
          continue;
        }

        view.dispatch(
          view.state.tr.setNodeMarkup($from.before(depth), undefined, {
            ...node.attrs,
            checked: false,
          }),
        );
        view.focus();
        return;
      }
    });
  }

  protected toggleBlockquote(): void {
    this.runCommand(() => this.editor?.action(callCommand(wrapInBlockquoteCommand.key)));
  }

  protected toggleCodeBlock(): void {
    this.runCommand(() => this.editor?.action(callCommand(createCodeBlockCommand.key)));
  }

  protected insertTable(): void {
    this.runCommand(() => this.editor?.action(callCommand(insertTableCommand.key, { row: 3, col: 3 })));
  }

  protected insertLink(): void {
    const href = window.prompt('輸入連結網址');
    if (!href) {
      return;
    }

    this.runCommand(() => {
      const editor = this.editor;
      if (!editor) {
        return;
      }

      const view = editor.action((ctx) => ctx.get(editorViewCtx));
      if (view.state.selection.empty) {
        editor.action(insert(`[${href}](${href})`, true));
        return;
      }

      editor.action(callCommand(toggleLinkCommand.key, { href }));
    });
  }

  protected insertImage(): void {
    if (this.destroyed || !this.editorReady || this.isDisabled || this.isReadonly) {
      return;
    }
    const src = window.prompt('輸入圖片網址');
    if (src) {
      this.insertMinioImage(src);
    }
  }

  protected triggerImageUpload(): void {
    if (this.destroyed || !this.editorReady || this.isDisabled || this.isReadonly || this.imageUploading) {
      return;
    }
    this.imageInput.nativeElement.click();
  }

  protected async onImageFileSelected(event: Event): Promise<void> {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (!file || this.destroyed || !this.editorReady || this.isDisabled || this.isReadonly || this.imageUploading) {
      return;
    }

    this.imageUploading = true;
    this.cdr.markForCheck();
    try {
      const url = await this.minioService.uploadImage(this.host(), this.path(), file);
      this.insertMinioImage(url);
      this.minioImages = [];
    } catch (error: unknown) {
      console.error('上傳圖片至 MinIO 失敗', error);
      if (!this.destroyed) {
        window.alert('上傳圖片失敗，請確認 MinIO 服務已啟動');
      }
    } finally {
      this.imageUploading = false;
      if (!this.destroyed) {
        this.cdr.markForCheck();
      }
    }
  }

  protected async toggleMinioImages(): Promise<void> {
    if (this.destroyed || !this.editorReady || this.isDisabled || this.isReadonly) {
      return;
    }
    this.showMinioImages = !this.showMinioImages;
    if (!this.showMinioImages || this.minioImagesLoading) {
      return;
    }

    this.minioImagesLoading = true;
    this.minioImages = [];
    this.cdr.markForCheck();
    try {
      const images = await this.minioService.listImages(this.host(), this.path());
      if (!this.destroyed) {
        this.minioImages = images;
      }
    } catch (error: unknown) {
      console.error('載入 MinIO 圖片失敗', error);
    } finally {
      this.minioImagesLoading = false;
      if (!this.destroyed) {
        this.cdr.markForCheck();
      }
    }
  }

  protected insertMinioImage(src: string): void {
    if (this.destroyed) {
      return;
    }
    this.runCommand(() => {
      const editor = this.editor;
      if (!editor) {
        return;
      }
      editor.action(callCommand(insertImageCommand.key, { src }));
      this.setMarkdown(editor.action(getMarkdown()), true);
      editor.action((ctx) => ctx.get(editorViewCtx).focus());
      this.onTouched();
      this.showMinioImages = false;
      this.cdr.markForCheck();
    });
  }

  protected undo(): void {
    this.runCommand(() => this.editor?.action(callCommand(undoCommand.key)));
  }

  protected redo(): void {
    this.runCommand(() => this.editor?.action(callCommand(redoCommand.key)));
  }

  protected toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
    this.cdr.markForCheck();
  }

  protected toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.fullscreenChange.emit(this.isFullscreen);
    this.cdr.markForCheck();
  }

  private async createEditor(): Promise<void> {
    const editor = Editor.make()
      .config((ctx) => {
        ctx.set(rootCtx, this.editorHost.nativeElement);
        ctx.set(defaultValueCtx, this.value);
        ctx.update(editorViewOptionsCtx, (options) => ({
          ...options,
          attributes: this.editorAttributes(),
          editable: () => !this.isDisabled && !this.isReadonly,
        }));
        ctx.get(listenerCtx)
          .markdownUpdated((_ctx, markdown) => {
            if (!this.applyingExternalValue) {
              this.setMarkdown(markdown, true);
            }
          })
          .blur(() => this.onTouched());
      })
      .use(commonmark)
      .use(gfm)
      .use(history)
      .use(clipboard)
      .use(listener);

    this.editor = editor;
    await editor.create();

    if (this.destroyed) {
      await editor.destroy();
      return;
    }

    this.editorReady = true;
    this.setMarkdown(this.value, false);
    this.updateEditableState();
    this.cdr.markForCheck();
  }

  private setMarkdown(markdown: string, emit: boolean): void {
    const valueChanged = markdown !== this.value;
    this.value = markdown;

    const editor = this.editor;
    if (!emit && this.editorReady && editor && editor.action(getMarkdown()) !== markdown) {
      this.applyingExternalValue = true;
      try {
        editor.action(replaceAll(markdown));
      } finally {
        this.applyingExternalValue = false;
      }
    }

    if (emit && valueChanged) {
      this.onChange(markdown);
      this.markdownChange.emit(markdown);
      this.onValidatorChange();
    }

    this.cdr.markForCheck();
  }

  private updateEditableState(): void {
    if (this.editorReady && this.editor) {
      const view = this.editor.action((ctx) => ctx.get(editorViewCtx));
      view.setProps({
        attributes: this.editorAttributes(),
        editable: () => !this.isDisabled && !this.isReadonly,
      });
    }
    this.cdr.markForCheck();
  }

  private toSafeHtml(icon: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(icon);
  }

  private editorAttributes(): Record<string, string> {
    return {
      'aria-label': this.editorPlaceholder,
      'aria-required': String(this.isRequired),
      'aria-readonly': String(this.isReadonly),
      'data-placeholder': this.editorPlaceholder,
      spellcheck: 'true',
    };
  }

  private runCommand(command: () => unknown): void {
    if (!this.editorReady || this.isDisabled || this.isReadonly) {
      return;
    }
    command();
  }

  /** 標籤文字 */
  readonly label = model<string>('');
  /**
   * @property {string} emojiLabel
   * @description 顯示在輸入欄位左側的 Emoji。
   */
  readonly emojiLabel = input('');

}
